/**
 * Support Service
 * API service for support tickets
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Ticket, TicketStats, TicketFilters, CreateTicketRequest, TicketMessage } from '../models/support.models';

@Injectable({
  providedIn: 'root'
})
export class SupportService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getStats(): Observable<TicketStats> {
    return this.http.get<TicketStats>(`${this.apiUrl}/support/stats`);
  }

  getTickets(
    filters: TicketFilters,
    page: number,
    pageSize: number
  ): Observable<{ tickets: Ticket[]; total: number }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (filters.search) {
      params = params.set('search', filters.search);
    }
    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.priority) {
      params = params.set('priority', filters.priority);
    }
    if (filters.category) {
      params = params.set('category', filters.category);
    }

    return this.http.get<{ tickets: Ticket[]; total: number }>(
      `${this.apiUrl}/support/tickets`,
      { params }
    );
  }

  getTicket(ticketId: string): Observable<Ticket> {
    return this.http.get<Ticket>(`${this.apiUrl}/support/tickets/${ticketId}`);
  }

  createTicket(request: CreateTicketRequest): Observable<Ticket> {
    const formData = new FormData();
    formData.append('subject', request.subject);
    formData.append('description', request.description);
    formData.append('category', request.category);
    formData.append('priority', request.priority);

    if (request.attachments) {
      request.attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });
    }

    return this.http.post<Ticket>(`${this.apiUrl}/support/tickets`, formData);
  }

  addMessage(ticketId: string, content: string, attachments?: File[]): Observable<TicketMessage> {
    const formData = new FormData();
    formData.append('content', content);

    if (attachments) {
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });
    }

    return this.http.post<TicketMessage>(
      `${this.apiUrl}/support/tickets/${ticketId}/messages`,
      formData
    );
  }

  closeTicket(ticketId: string): Observable<Ticket> {
    return this.http.patch<Ticket>(
      `${this.apiUrl}/support/tickets/${ticketId}`,
      { status: 'closed' }
    );
  }

  reopenTicket(ticketId: string): Observable<Ticket> {
    return this.http.patch<Ticket>(
      `${this.apiUrl}/support/tickets/${ticketId}`,
      { status: 'open' }
    );
  }
}

